import { Category, getCategories } from '@/api/category-api';
import { CategoryManagerModal } from '@/components/products/CategoryManagerModal';
import { useCollectionData } from '@/hooks/use-api';
import { type ProductFormValues, type ProductUpdateValues } from '@/types/product-type';
import { Box, Button, Grid, MenuItem, TextField } from '@mui/material';
import { FolderPlus } from 'lucide-react';
import React, { useState } from 'react';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import { MySection } from '../../custom/my-section';

interface ProductBasicInformationSectionProps {
  control: Control<ProductFormValues | ProductUpdateValues>;
  register: UseFormRegister<ProductFormValues | ProductUpdateValues>;
  errors: FieldErrors<ProductFormValues | ProductUpdateValues>;
  inline?: boolean;
  onCodeBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export function ProductBasicInformationSection({
  control,
  register,
  errors,
  inline,
  onCodeBlur,
}: ProductBasicInformationSectionProps) {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const { data: categoriesData } = useCollectionData<Category[]>(
    '/categories',
    getCategories
  );
  const categories = categoriesData ?? [];

  const getCategoryLabel = (cat: Category) => {
    const parent = cat.parentId
      ? categories.find((c) => String(c.id) === String(cat.parentId))
      : null;
    const prefix = cat.icon ? `${cat.icon} ` : '';
    return parent ? `${parent.name} / ${prefix}${cat.name}` : `${prefix}${cat.name}`;
  };

  const content = (
    <>
      <Grid container gap={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Product Code"
            placeholder="e.g. SKU-001 (Use For Facebook Sale)"
            InputLabelProps={{ shrink: true }}
            {...register('code' as any, {
              onBlur: onCodeBlur,
            })}
            error={!!errors.code}
            helperText={errors.code?.message as string}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Name"
            placeholder="e.g. Premium Hoodie"
            InputLabelProps={{ shrink: true }}
            {...register('name' as any)}
            error={!!errors.name}
            helperText={errors.name?.message as string}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={'categoryId' as any}
            control={control}
            render={({ field }) => (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <TextField
                  select
                  fullWidth
                  label="Category (Optional)"
                  InputLabelProps={{ shrink: true }}
                  value={field.value ? String(field.value) : ''}
                  onChange={(e) =>
                    field.onChange(e.target.value ? String(e.target.value) : null)
                  }
                  SelectProps={{
                    displayEmpty: true,
                  }}
                  helperText="Leave as Uncategorized for flat catalog, or assign a category for Storefront / Mini App tabs"
                >
                  <MenuItem value="">
                    <em>None (Uncategorized / Default)</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={String(cat.id)}>
                      {getCategoryLabel(cat)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setShowCategoryModal(true)}
                  sx={{
                    height: 56,
                    px: 2,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    borderRadius: 1.5,
                    flexShrink: 0,
                  }}
                  startIcon={<FolderPlus size={16} />}
                >
                  Categories
                </Button>

                <CategoryManagerModal
                  open={showCategoryModal}
                  onOpenChange={setShowCategoryModal}
                  onCategoryCreated={(newCat) => {
                    field.onChange(String(newCat.id));
                  }}
                />
              </Box>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            placeholder="Describe the product"
            InputLabelProps={{ shrink: true }}
            multiline
            minRows={4}
            {...register('description' as any)}
            error={!!errors.description}
            helperText={errors.description?.message as string}
          />
        </Grid>
      </Grid>
    </>
  );

  return inline ? (
    content
  ) : (
    <MySection title="Basic information" description="Provide essential product details.">
      {content}
    </MySection>
  );
}
